/**
 * Identifies which action-needed-relevant field changed inside the library,
 * so the app can apply a **single-field** update instead of re-syncing the
 * entire `CostReviewOpenResponse`.
 *
 * Each enum value encodes both the parent object and property name
 * so the mapper can route the value to the correct target.
 */
export enum ActionNeededField {
  // ═════════════════════════════════════════════════════════════════════
  //  Automation / Electrical / Mechanical  (13 fields)
  // ═════════════════════════════════════════════════════════════════════

  // ── CostReviewMarginExceptions (6) ──────────────────────────────────
  MarginReasonForException          = 'marginExceptions.reasonForException',
  MarginApproverEmailId             = 'marginExceptions.approverEmailId',
  MarginSmOrBgmApprovalReceived     = 'marginExceptions.smOrBgmApprovalRecieved',
  MarginSmApprovalReceived          = 'marginExceptions.smApprovalReceived',
  MarginIsReasonForExceptionApplicable = 'marginExceptions.isReasonForExceptionApplicable',
  MarginIsBGMApprovalApplicable     = 'marginExceptions.isBGMApprovalApplicable',

  // ── CostReviewBaseAttributes (4) ────────────────────────────────────
  IsEngineerExceptionApplicable     = 'attributes.isEngineerExceptionApplicable',
  EngineerApprovalReceived          = 'attributes.engineerApprovalReceived',
  EngineerApproverEmail             = 'attributes.engineerApproverEmail',
  EngineerReasonForException        = 'attributes.engineerReasonForException',

  // ── CostReviewPrefab (3) ───────────────────────────────────────────
  PrefabReasonForException          = 'prefab.reasonForException',
  PrefabIsPrefabControllerVisible   = 'prefab.isPrefabControllerVisible',
  PrefabIsReasonForExceptionVisible = 'prefab.isReasonForExceptionVisible',

  // ═════════════════════════════════════════════════════════════════════
  //  Fire  (10 fields)
  // ═════════════════════════════════════════════════════════════════════

  // ── CostReviewMarginExceptions (Fire) (6) ───────────────────────────
  FireMarginReasonForException              = 'fire.marginExceptions.reasonForException',
  FireMarginApproverEmailId                 = 'fire.marginExceptions.approverEmailId',
  FireMarginSmOrBgmApprovalReceived         = 'fire.marginExceptions.smOrBgmApprovalRecieved',
  FireMarginSmApprovalReceived              = 'fire.marginExceptions.smApprovalReceived',
  FireMarginIsReasonForExceptionApplicable  = 'fire.marginExceptions.isReasonForExceptionApplicable',
  FireMarginIsBGMApprovalApplicable         = 'fire.marginExceptions.isBGMApprovalApplicable',

  // ── CostReviewSummary (Fire) (4) ───────────────────────────────────
  FireIsZteApprovalVisible          = 'fire.summary.isZteApprovalVisible',
  FireZteApprovalReceived           = 'fire.summary.zteApprovalReceived',
  FireSmApprovalReceived            = 'fire.summary.smApprovalRecieved',
  FireIsFireTBEVisibility           = 'fire.summary.isFireTBEVisibility',

  // ═════════════════════════════════════════════════════════════════════
  //  Security  (8 fields)
  // ═════════════════════════════════════════════════════════════════════

  // ── CostReviewMarginExceptions (Security) (6) ──────────────────────
  SecurityMarginReasonForException              = 'security.marginExceptions.reasonForException',
  SecurityMarginApproverEmailId                 = 'security.marginExceptions.approverEmailId',
  SecurityMarginSmOrBgmApprovalReceived         = 'security.marginExceptions.smOrBgmApprovalRecieved',
  SecurityMarginSmApprovalReceived              = 'security.marginExceptions.smApprovalReceived',
  SecurityMarginIsReasonForExceptionApplicable  = 'security.marginExceptions.isReasonForExceptionApplicable',
  SecurityMarginIsBGMApprovalApplicable         = 'security.marginExceptions.isBGMApprovalApplicable',

  // ── CostReviewSecurityEnUsAttributes (Security) (2) ────────────────
  SecurityIsLaborTDCPercentExceeds  = 'security.isLaborTDCPercentExceeds',
  SecurityReasonForLaborVariance    = 'security.reasonForLaborVariance',
}

/**
 * Payload emitted by the library when a single action-needed field changes.
 *
 * The app subscribes to these and applies the value to the matching
 * property on its own `CostReviewOpenResponse`.
 */
export interface ActionNeededFieldChange {
  /** Which field was modified. */
  field: ActionNeededField;
  /** The new value. */
  value: string | number | boolean;
}
